/**
 * Tutorial step script.
 *
 * Each step describes:
 *   - copy shown in the overlay
 *   - which DOM target to highlight (looked up via `data-hud-id` /
 *     `data-tutorial-target` / seat indices on the canvas)
 *   - how the user advances (manual click, automatic on event, branching)
 *
 * Side-effects (mutating seats, opening drawers, pushing rewards) live in
 * `TutorialOverlay` and are dispatched on step entry/exit. Keeping the
 * descriptive script here means the overlay code stays focused on
 * spotlight/typewriter mechanics.
 */

export type TutorialStepId =
  | 'view-pick'
  | 'intro-board'
  | 'yield-panel'
  | 'rewards-stream'
  | 'find-floor'
  | 'set-price'
  | 'set-price-rent'
  | 'confirm-tx'
  | 'own-tile-blue'
  | 'holder-chat'
  | 'competitor-buyouts'
  | 'defend-or-leave'
  | 'path-leave'
  | 'path-defend'
  | 'outro';

export type AdvanceMode =
  | 'manual'         // user clicks the Next button in the overlay
  | 'auto'           // overlay auto-advances after a delay
  | 'event'          // step listens for a specific event (drawer success, branch pick)
  | 'branch';        // step ends with two CTAs (defend / leave)

/** Where the dialogue dock sits on screen for a given step.
 *  - `bottom`: anchored to the bottom-centre of the viewport (default).
 *  - `near-panel`: hugs the **left edge of the buyout drawer** on
 *    sm+ viewports so the user can read the rent beat next to the
 *    summary it's describing. Falls back to top-centre on smaller
 *    viewports where the drawer covers the full width.
 *  Other entries on the type are reserved (and currently unused) so
 *  step authors can describe intent without coupling to layout. */
export type DialoguePosition = 'bottom' | 'near-panel' | 'left' | 'right' | 'center';

export interface TutorialStepDefinition {
  id: TutorialStepId;
  /** Optional title rendered above the body copy. */
  title?: string;
  /** Body copy shown in the typewriter panel. */
  body: string;
  /** Final-step button label override. Other steps use the shared "Next". */
  cta?: string;
  /** DOM hook to spotlight. Looked up as `[data-hud-id="..."]` first, then
   *  `[data-tutorial-target="..."]`. `null` means no spotlight (full clear). */
  highlightTarget?: string | null;
  /** When true, the dim overlay paints around the highlight target. */
  dim?: boolean;
  /** How the user advances. */
  advance: AdvanceMode;
  /** Auto-advance delay in ms (only relevant when `advance: 'auto'`). */
  autoAdvanceMs?: number;
  /** Where the dialogue dock sits — see `DialoguePosition`. Defaults to
   *  `'bottom'` when omitted. */
  panelPosition?: DialoguePosition;
}

export const TUTORIAL_STEPS: readonly TutorialStepDefinition[] = [
  {
    id: 'view-pick',
    title: 'Pick how you see the board',
    body: 'Cinematic 3D for the full experience, or a flat top-down 2D for a lighter view. You can swap any time from the graphics menu.',
    advance: 'manual',
    panelPosition: 'center',
  },
  {
    // Step 1 in the user-facing script: introduce the board itself.
    id: 'intro-board',
    title: 'Welcome to the board',
    body: 'Every board has a grid of tiles. Each tile is a piece of land.',
    advance: 'manual',
    panelPosition: 'bottom',
  },
  {
    // Step 2: yield panel.
    //
    // Mobile: the highlight ring lands on the `Yield` chip in the
    // panel rail (`data-tutorial-target="yield-pill"`) rather than
    // the panel overlay itself. The panel still auto-opens (see the
    // `tutorialStepId` driver in `LobbyClient`) so the body copy has
    // visible context, but the spotlight reads as "tap this chip" —
    // which matches the user's mental model of the redesigned mobile
    // chrome (panel pills are the primary navigation, panels are
    // their content). Desktop falls through to the `data-hud-id`
    // fallback in `findHighlightElement`, so the floating Yield HUD
    // panel still gets the ring there.
    id: 'yield-panel',
    title: 'Holding land unlocks yield',
    body: 'Boards can generate yield from protocol fees, memecoins and more. Holding land entitles you to rewards from the yield sources shown here.',
    highlightTarget: 'yield-pill',
    dim: true,
    advance: 'manual',
    panelPosition: 'bottom',
  },
  {
    // Step 3: rewards panel. Same pill-not-panel pattern as the yield
    // step above (mobile chip vs desktop floating panel — see the
    // yield-panel step's note for why the target shape changed).
    id: 'rewards-stream',
    title: 'Rewards are flowing in',
    body: 'Rewards are tracked in the activity panel in real-time. Historical performance can also be seen here.',
    highlightTarget: 'rewards-pill',
    dim: true,
    advance: 'manual',
    panelPosition: 'bottom',
  },
  {
    // Step 4: find-floor. The user explicitly asked for the rest of the
    // board to be darkened so only the floor tile + 3D blue ring read —
    // `dim: true` + `useSpotlightRect` returning the floor tile's
    // projected AABB lets `OverlayShell` paint a circular cutout
    // around the tile (matching the in-canvas ring shape) while the
    // window-level input guard keeps clicks scoped to the floor seat.
    id: 'find-floor',
    title: 'Time to claim some land',
    body: 'Let\u2019s start by taking over a tile. Here\u2019s the cheapest \u201cfloor\u201d tile \u2014 go ahead and click it.',
    highlightTarget: 'tutorial-floor-tile',
    dim: true,
    advance: 'event',
    panelPosition: 'bottom',
  },
  {
    // Step 5: set-price. Spotlight the 1.2x preset chip directly so the
    // user knows which button to tap. The overlay listens for a click on
    // this preset to advance.
    id: 'set-price',
    title: 'Set your price',
    body: 'This land is priced at $5.00 and we think it\u2019s undervalued. Let\u2019s take it over and set a new price 1.2x higher.',
    highlightTarget: 'tutorial-price-preset-1.2x',
    dim: true,
    advance: 'event',
    panelPosition: 'bottom',
  },
  {
    // Step 6: set-price-rent. Per the latest script the dialogue moves
    // close to the buyout panel here (`panelPosition: 'near-panel'`)
    // and the spotlight tracks the summary block (Buyout Cost down to
    // Total cost — `tutorial-buyout-summary` is wired in the drawer).
    id: 'set-price-rent',
    title: 'Rent follows your price',
    body: 'The game works by paying rent based on the price you set. This board charges 5%/week of the price set, so the cost of holding is $0.30 for 7 days.',
    highlightTarget: 'tutorial-buyout-summary',
    dim: true,
    advance: 'manual',
    panelPosition: 'near-panel',
  },
  {
    // Step 7: confirm-tx. Highlight stays on the Take Over CTA inside
    // the drawer so the next-action remains clear.
    //
    // `panelPosition: 'near-panel'` (instead of the original `'bottom'`
    // the script asked for) is a mobile fix: at the bottom-centre
    // anchor the dialogue lay flush over the Take Over button on a
    // phone, leaving the user nothing to tap. `near-panel`'s
    // sub-`lg` fallback is `top-4` (top-centre), which clears the
    // button and reads as a continuation of the previous
    // (`set-price-rent`) step rather than a re-anchor. On `lg+` the
    // dialogue still sits to the left of the buyout drawer — no
    // overlap with the CTA there either, so the desktop layout
    // doesn't regress.
    id: 'confirm-tx',
    title: 'Take over the tile',
    body: 'We think the rewards from holding land on this board will outperform the cost. Take it over by paying the listing price + your $0.30 deposit to cover 7 days of rent.',
    highlightTarget: 'tutorial-buyout-submit',
    dim: true,
    advance: 'event',
    panelPosition: 'near-panel',
  },
  {
    // Step 8: own-tile-blue. After the take-over the user explicitly
    // asked to see the slab wearing the "owned tile" look (obsidian
    // plinth + natural blue edge ring). `dim: false` keeps the board
    // bright and the highlight on the My Tiles panel just paints the
    // blue rounded outline (no dim mask). The natural owned-tile
    // treatment carries the "this is your tile" read.
    id: 'own-tile-blue',
    title: 'You own the land',
    body: 'You now own this land. You can change its price at any time to exit or defend. The game requires constant market making to balance rewards and costs.',
    highlightTarget: 'mytiles',
    dim: false,
    advance: 'manual',
    panelPosition: 'bottom',
  },
  {
    // Step 9 (NEW per latest user request): holder-chat. The lobby
    // auto-opens the chat panel for any seat-holder, so by this step
    // the floating/docked chat is already mounted. We just need to
    // call attention to it. `dim: true` so the rest of the lobby
    // chrome quiets down while the user reads.
    id: 'holder-chat',
    title: 'Coordinate with other holders',
    body: 'All land holders on the board get access to a private chat. Users can talk to the creator and coordinate with other land holders who are aligned with increasing land value.',
    // Mobile pill highlight (see the yield-panel step's note). The
    // chat panel still auto-opens via the tutorial step driver so
    // the body copy lines up with what the viewer can see.
    highlightTarget: 'chat-pill',
    dim: true,
    advance: 'manual',
    panelPosition: 'bottom',
  },
  {
    // Step 10: competitor-buyouts. The user asked for a 5-tile
    // cascade ("there should be 5 buyouts not 10") that still
    // builds tension before the defend/exit decision: the
    // viewer's tile must be visibly approaching the floor by the
    // time the choice card lands. We sweep 5 cheap-band tiles
    // (one every 500 ms) which pushes the viewer's $6 listing
    // from sorted rank 9 → rank 4 — the visual "approaching the
    // floor" beat the new copy references. The narrative is
    // preserved by the matching shrink of `CHEAP_BAND_COUNT`
    // (14 → 9): there are still exactly 4 cheaper tiles left
    // around the viewer when the choice card lands.
    //
    // Pacing budget mirrors `path-defend` exactly so the two waves
    // read with the same tempo and the user picks up that the same
    // mechanic is firing on both sides of the decision:
    //   1500 ms PRE_ROLL_MS (camera pull-back tween is 1500 ms)
    // + 2000 ms staggered crumbles (5 × 500 ms; last triggers at
    //                               t = 2000 ms relative to wave
    //                               start)
    // + 1740 ms BUYOUT_TOTAL_MS tail of the 5th crumble's regrow
    //                            + avatar fade
    // + 2000 ms post-wave pause so the user can absorb the cascade
    //                            and clock that they're now near the
    //                            floor before the choice card lands
    //  ≈ 7240 ms total
    id: 'competitor-buyouts',
    title: 'The board is heating up',
    body: 'The board is generating more rewards and now others are taking over land\u2026',
    highlightTarget: null,
    dim: false,
    advance: 'auto',
    autoAdvanceMs: 7240,
    panelPosition: 'bottom',
  },
  {
    // Per the latest script, the decision dialogue stays anchored at
    // the bottom of the viewport (not centred) so the user can still
    // see their owned tile while they pick — centring the dialogue
    // covered the slab during the moment that's *literally* about
    // their slab. `panelPosition: 'bottom'` keeps the dialogue
    // hugging the bottom edge while the camera frames the tile in
    // the upper portion of the screen.
    id: 'defend-or-leave',
    title: 'Defend or exit?',
    body: 'Your land is now approaching the floor and you need to make a decision \u2014 risk being taken over and exiting at your price, or increase your price and defend your land.',
    advance: 'branch',
    panelPosition: 'bottom',
  },
  {
    // Per the latest script the exit path is now a 5-tile cascade
    // ("we show 5 quick buyouts that culminates in the user's tile
    // being bought and the congratulations screen appearing"). The
    // viewer is at sorted rank 4 (position 5) coming into this step,
    // so the wave buys out the 4 cheaper cheap-band survivors first
    // and lands the 5th crumble on the viewer's own tile — the camera
    // is locked on the viewer (`followViewer: false`) so all 5
    // shatters land in shot.
    //
    // Pacing budget:
    //   1000 ms PRE_ROLL_MS (camera settles on viewer's tile)
    // + 2000 ms staggered crumbles (5 × 500 ms; last triggers at
    //                               t = 2000 ms relative to wave
    //                               start — that's the viewer's
    //                               own tile)
    // + 1740 ms BUYOUT_TOTAL_MS tail of the viewer's crumble (regrow
    //                            + avatar fade still play after the
    //                            trigger)
    // + 1000 ms post-buyout pause before the outro takes over (per
    //                            the user's "1000 ms after buyout
    //                            animation complete" brief)
    //  ≈ 5740 ms total
    id: 'path-leave',
    body: 'Buyers are sweeping the cheapest tiles \u2014 yours is up next.',
    advance: 'auto',
    autoAdvanceMs: 5740,
    panelPosition: 'bottom',
  },
  {
    // Pacing budget — the latest script trims the wave from 10
    // buyouts to 5 ("there should be 5 buyouts not 10 in both
    // instances"). They still fan out linearly at 500 ms apart
    // followed by a 2 s "take it in" pause before the outro lands:
    //   1300 ms PRE_ROLL_MS (camera settle + beat before wave)
    // + 2000 ms staggered crumbles (5 × 500 ms; last fires at
    //                               t = 2000 ms relative to wave
    //                               start)
    // + 1740 ms BUYOUT_TOTAL_MS tail of the LAST crumble (the 5th
    //                            buyout's regrow + avatar fade
    //                            still play after the trigger)
    // + 2000 ms post-wave pause so the user can absorb the
    //                            land-grab cascade
    //  ≈ 7040 ms total
    id: 'path-defend',
    body: 'You raised your price \u2014 and the market moves with you. Watch the next cheapest tiles get snapped up.',
    advance: 'auto',
    autoAdvanceMs: 7040,
    panelPosition: 'bottom',
  },
  {
    // Single celebratory outro — the body copy is branched by
    // `state.branch` inside `TutorialOverlay`, so this string is
    // never displayed verbatim. The overlay also swaps in the two
    // CTAs (Lobby / Play Again) and drops the Skip button.
    id: 'outro',
    title: 'Tutorial complete',
    body: 'You\u2019re ready to play.',
    advance: 'manual',
    panelPosition: 'center',
  },
] as const;

export const TUTORIAL_STEP_IDS = TUTORIAL_STEPS.map((s) => s.id);

export function findTutorialStep(id: TutorialStepId): TutorialStepDefinition {
  return TUTORIAL_STEPS.find((s) => s.id === id) ?? TUTORIAL_STEPS[0];
}
